import React, { useState, useEffect } from "react";
import PanelHeader from "components/PanelHeader/PanelHeader.js";
import { Tabs, Tab } from "react-bootstrap";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Spinner,
} from "reactstrap";
import { MDBDataTableV5 } from "mdbreact";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import { BASE_URL } from "utils/constants";
import { Link, useParams } from "react-router-dom";
import "./grading.css";

// Helper to determine if a string or HTML snippet has meaningful content (not empty or just "NA")
const hasValidContent = (content) => {
  if (!content) return false;
  if (typeof content !== "string") return true;

  const trimmed = content.trim();
  if (
    trimmed === "" ||
    trimmed === "NA" ||
    trimmed === '"NA"' ||
    trimmed === "N/A" ||
    trimmed === "null" ||
    trimmed === "undefined"
  ) {
    return false;
  }

  // Strip HTML tags and entities to verify whether actual text exists
  const textOnly = trimmed
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .trim();

  if (
    textOnly === "" ||
    textOnly === "NA" ||
    textOnly === '"NA"' ||
    textOnly === "N/A"
  ) {
    // Retain content if it includes embedded media (images, tables, iframes, etc.)
    if (/<(img|iframe|video|audio|table|svg)/i.test(trimmed)) {
      return true;
    }
    return false;
  }

  return true;
};

// Check if a string is a valid web URL or Google Drive link
const isValidUrl = (str) => {
  if (!str || typeof str !== "string") return false;
  const trimmed = str.trim();
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.includes("drive.google.com")
  );
};

// Reusable Empty State Card with rich visuals, context-aware icons, and helpful tips
const TabEmptyState = ({
  icon,
  iconColor = "#2c7be5",
  iconBg = "rgba(44, 123, 229, 0.1)",
  badge,
  title,
  description,
  tip,
  action,
}) => {
  return (
    <div className="course-empty-state-card">
      <div
        className="course-empty-icon-wrap"
        style={{
          backgroundColor: iconBg,
          color: iconColor,
        }}
      >
        <i className={icon}></i>
      </div>

      {badge && (
        <span
          className="course-empty-badge"
          style={{
            backgroundColor: iconBg,
            color: iconColor,
          }}
        >
          {badge}
        </span>
      )}

      <h3 className="course-empty-title">{title}</h3>
      <p className="course-empty-desc">{description}</p>

      {tip && (
        <div className="course-empty-tip">
          <i className="now-ui-icons travel_info"></i>
          <div>{tip}</div>
        </div>
      )}

      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};

// Course Metadata Summary Component
const CourseInfoGrid = ({ course }) => {
  const infoTiles = [
    { label: "Course Name", value: course?.name || "N/A" },
    { label: "Course Code", value: course?.coursecode || "Not Specified" },
    {
      label: "Credit Hours",
      value: course?.credithours || course?.hours || "N/A",
    },
    {
      label: "Contact Hours",
      value: course?.contacthours ? `${course.contacthours} hrs` : "N/A",
    },
    {
      label: "Lecture Hours",
      value: course?.lechours ? `${course.lechours} hrs/wk` : "N/A",
    },
    {
      label: "Lab Hours",
      value: course?.labhours ? `${course.labhours} hrs/wk` : "N/A",
    },
  ];

  return (
    <div className="course-content-card mt-4">
      <div className="course-content-card-header">
        <h5 className="course-content-card-title">
          <i className="now-ui-icons business_badge text-primary"></i> Course
          Details &amp; Information
        </h5>
      </div>
      <div className="course-content-card-body p-3">
        <div className="course-info-grid">
          {infoTiles.map((tile, idx) => (
            <div className="course-info-tile" key={idx}>
              <span className="course-info-label">{tile.label}</span>
              <p className="course-info-value">{tile.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CourseGrading = () => {
  const [activeTab, setActiveTab] = useState("home");
  const [loadingGrades, setLoadingGrades] = useState(true);
  const [course, setCourse] = useState({});
  const [totScores, setTotScores] = useState(0);
  const [totMarks, setTotMarks] = useState(0);
  const { id } = useParams();
  const mySwal = withReactContent(Swal);

  const [examsData, setExamsData] = useState({
    columns: [
      { label: "Name", field: "name" },
      { label: "Due", field: "ddate" },
      { label: "Status", field: "status" },
      { label: "Score", field: "score" },
      { label: "Out Of", field: "outof" },
      { label: "Preview", field: "preview" },
    ],
    rows: [],
  });

  const sectionContent = (html) => {
    return (
      <div
        className="course-rich-content"
        dangerouslySetInnerHTML={{ __html: html }}
      ></div>
    );
  };

  const handleExamStatistics = () => {
    mySwal.fire({
      html: (
        <ul className="fs-6 fw-bold w-100 mb-5 list-unstyled">
          <li>Low : 15</li>
          <li>Mean : 20</li>
          <li>High : 25</li>
        </ul>
      ),
      title: "Exam Statistics",
      icon: "info",
      iconHtml: "!",
      showConfirmButton: false,
    });
  };

  const getLmsGrades = (originArr) => {
    let grdsArr = [];
    let cIndex = 0;
    originArr.forEach((section) => {
      const catArray = section["grading_category"];
      section["period"]?.forEach((period) => {
        period["assignment"]?.forEach((ass) => {
          cIndex = catArray?.findIndex((gc) => gc.id == ass.category_id);
          ass["category"] =
            cIndex != -1 ? catArray[cIndex].title : "Assessment";
          grdsArr.push(ass);
        });
      });
    });
    return grdsArr;
  };

  useEffect(() => {
    (async () => {
      try {
        let res = await axios.post(`${BASE_URL}/student/getcourse.php`, {
          id: id,
        });
        setCourse(res.data.data || {});
      } catch {
        Swal.fire("Error while fetching course information!", "", "error");
      }
    })();
  }, [id]);

  const getcourseGrades = () => {
    setLoadingGrades(true);
    axios
      .post(`${BASE_URL}/exam/getcoursegrades.php`, { course_id: id })
      .then((res) => {
        let exams = [],
          exOfLms = [];

        const rawQuizzes = res.data.data?.quizez;
        const rawHomeworks = res.data.data?.homeworks;
        const rawCourses = res.data.data?.courses;

        const qzs =
          Array.isArray(rawQuizzes) && rawQuizzes.length > 0
            ? rawQuizzes.filter(
                (exam) =>
                  exam.subject_id == id &&
                  (exam.status === "Graded" || exam.status === "Missed")
              )
            : [];

        const hws =
          Array.isArray(rawHomeworks) && rawHomeworks.length > 0
            ? rawHomeworks.filter(
                (exam) =>
                  exam.subject_id == id &&
                  (exam.status === "Graded" || exam.status === "Missed")
              )
            : [];

        const matchingCourse = Array.isArray(rawCourses)
          ? rawCourses.find((c) => c.id == id)
          : null;
        const examsFromLms = matchingCourse?.sectionsArray || [];

        exams = [...qzs, ...hws];

        if (examsFromLms?.length > 0) {
          exOfLms = getLmsGrades(examsFromLms)?.filter(
            (ex) => ex.grade != null
          );
        }

        if (exams?.length > 0 || exOfLms?.length > 0) {
          setExamsData((prevState) => {
            const newState = { ...prevState };
            let score = 0,
              totalMark = 0,
              ArrayQuestions = [],
              ArrayGraded = [],
              totalMarks = 0,
              totalScores = 0;

            let newRows = exams?.map((exam) => {
              try {
                ArrayQuestions = exam.questions ? JSON.parse(exam.questions) : [];
              } catch (e) {
                ArrayQuestions = [];
              }

              try {
                ArrayGraded = exam.answerdquestions
                  ? JSON.parse(exam.answerdquestions)
                  : [];
              } catch (e) {
                ArrayGraded = [];
              }

              score =
                exam.status === "Graded"
                  ? ArrayGraded?.reduce((total, obj) => +obj.score + total, 0)
                  : 0;
              totalMark = ArrayQuestions?.reduce(
                (total, obj) => +obj.totalMark + total,
                0
              );
              totalScores += score;
              totalMarks += totalMark;
              setTotScores(totalScores);
              setTotMarks(totalMarks);

              return {
                ...exam,
                name: (
                  <>
                    {exam.examname}
                    <br />
                    <small className="fw-bold text-danger">{exam.type}</small>
                  </>
                ),
                ddate: exam.enddate,
                status: exam.status,
                score: score,
                outof: totalMark,
                preview: (
                  <>
                    <a
                      href={exam.examlink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary text-center fs-6 fw-bold py-1 px-3"
                    >
                      Preview
                    </a>
                    <a
                      className="ms-3"
                      style={{ textDecoration: "none", cursor: "pointer" }}
                      onClick={() => handleExamStatistics()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="25"
                        fill="currentColor"
                        className="bi bi-info-circle-fill text-info"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                      </svg>
                    </a>
                  </>
                ),
              };
            });

            let newRows2 = exOfLms?.map((exam) => {
              score = exam.grade;
              totalMark = exam.max_points;
              totalScores += score;
              totalMarks += totalMark;
              setTotScores(totalScores);
              setTotMarks(totalMarks);

              return {
                ...exam,
                name: (
                  <>
                    {exam.category}
                    <br />
                    <small className="fw-bold text-danger">{exam.type}</small>
                  </>
                ),
                ddate: new Date(exam.timestamp * 1000)?.toLocaleString(),
                status: "Graded",
                score: score,
                outof: totalMark,
              };
            });

            let totalRows = (newRows || []).concat(newRows2 || []);
            return { ...newState, rows: totalRows };
          });
        } else {
          setExamsData((prevState) => ({ ...prevState, rows: [] }));
          setTotScores(0);
          setTotMarks(0);
        }
      })
      .catch((e) => {
        console.error("Error loading exams:", e);
        setExamsData((prevState) => ({ ...prevState, rows: [] }));
      })
      .finally(() => {
        setLoadingGrades(false);
      });
  };

  useEffect(() => {
    getcourseGrades();
  }, [id]);

  const driveUrl = isValidUrl(course?.drivelink)
    ? course.drivelink.startsWith("http")
      ? course.drivelink
      : `https://${course.drivelink}`
    : null;

  const lmsUrl = (() => {
    const candidate = course?.lmslink || course?.schoolgylink;
    if (!candidate || typeof candidate !== "string") return null;
    const trimmed = candidate.trim();
    // Exclude dummy numbers (e.g. "1", "22"), "none", "NA", or obsolete schoology links
    if (
      !trimmed ||
      trimmed === "none" ||
      trimmed === "NA" ||
      /^\d+$/.test(trimmed) ||
      trimmed.toLowerCase().includes("schoology")
    ) {
      return null;
    }
    return trimmed.startsWith("http://") || trimmed.startsWith("https://")
      ? trimmed
      : `https://${trimmed}`;
  })();

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content m-0 p-0">
        <div className="course-container">
          {/* Course Header Banner */}
          <div className="course-header-banner">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <Link
                  to="/course"
                  className="btn btn-outline-primary btn-round px-3 py-2 fw-bold d-inline-flex align-items-center"
                >
                  <i className="now-ui-icons arrows-1_minimal-left me-1"></i>{" "}
                  Back
                </Link>
                <div>
                  <h2 className="course-header-title">
                    {course?.name || "Course Details"}
                  </h2>
                  <div className="d-flex flex-wrap align-items-center">
                    {course?.coursecode && (
                      <span className="course-meta-badge badge-primary">
                        <i className="now-ui-icons business_badge"></i>
                        {course.coursecode}
                      </span>
                    )}
                    {(course?.credithours || course?.hours) && (
                      <span className="course-meta-badge">
                        <i className="now-ui-icons education_paper"></i>
                        {course?.credithours || course?.hours} Credit Hours
                      </span>
                    )}
                    {lmsUrl && (
                      <a
                        href={lmsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="course-meta-badge badge-success text-decoration-none"
                      >
                        <i className="now-ui-icons ui-1_send"></i> LMS Link
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Course Tabs Navigation */}
          <Tabs
            id="course-grading-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="course-tabs-nav"
          >
            {/* 1. Home Tab */}
            <Tab
              eventKey="home"
              title={
                <span>
                  <i className="now-ui-icons education_paper"></i> Home
                </span>
              }
            >
              <div className="pt-2">
                {hasValidContent(course?.coursedescription) ? (
                  <>
                    <div className="course-content-card">
                      <div className="course-content-card-header">
                        <h5 className="course-content-card-title">
                          <i className="now-ui-icons education_paper text-primary"></i>
                          Course Overview &amp; Description
                        </h5>
                      </div>
                      <div className="course-content-card-body">
                        {sectionContent(course.coursedescription)}
                      </div>
                    </div>
                    <CourseInfoGrid course={course} />
                  </>
                ) : (
                  <>
                    <TabEmptyState
                      icon="now-ui-icons education_paper"
                      iconColor="#2c7be5"
                      iconBg="rgba(44, 123, 229, 0.1)"
                      badge="Course Overview"
                      title="No Course Description Available"
                      description="A detailed description or overview has not been provided yet for this course."
                      tip="Review the Syllabus or Modules tabs for curriculum details, or check back later once updated by the course instructor."
                    />
                    <CourseInfoGrid course={course} />
                  </>
                )}
              </div>
            </Tab>

            {/* 2. Announcements Tab */}
            <Tab
              eventKey="announcements"
              title={
                <span>
                  <i className="now-ui-icons ui-1_bell-53"></i> Announcements
                </span>
              }
            >
              <div className="pt-2">
                <TabEmptyState
                  icon="now-ui-icons ui-1_bell-53"
                  iconColor="#ff3636"
                  iconBg="rgba(255, 54, 54, 0.1)"
                  badge="Course Notices"
                  title="No Announcements Yet"
                  description="There are currently no active announcements or bulletins posted for this course."
                  tip="Check back periodically for important notices, exam reminders, assignment deadlines, and schedule updates from your instructor."
                />
              </div>
            </Tab>

            {/* 3. Syllabus Tab */}
            <Tab
              eventKey="syllabus"
              title={
                <span>
                  <i className="now-ui-icons files_single-copy-04"></i> Syllabus
                </span>
              }
            >
              <div className="pt-2">
                {hasValidContent(course?.coursesyllubus) ? (
                  <div className="course-content-card">
                    <div className="course-content-card-header">
                      <h5 className="course-content-card-title">
                        <i className="now-ui-icons files_single-copy-04 text-primary"></i>
                        Course Syllabus &amp; Curriculum Outline
                      </h5>
                    </div>
                    <div className="course-content-card-body">
                      {sectionContent(course.coursesyllubus)}
                    </div>
                  </div>
                ) : (
                  <TabEmptyState
                    icon="now-ui-icons files_single-copy-04"
                    iconColor="#18ce0f"
                    iconBg="rgba(24, 206, 15, 0.1)"
                    badge="Curriculum &amp; Policies"
                    title="No Syllabus Published"
                    description="The syllabus for this course has not been uploaded or published yet."
                    tip="The syllabus outlines grading criteria, weekly milestones, course expectations, and required reading materials once available."
                  />
                )}
              </div>
            </Tab>

            {/* 4. Modules Tab */}
            <Tab
              eventKey="modules"
              title={
                <span>
                  <i className="now-ui-icons design_app"></i> Modules
                </span>
              }
            >
              <div className="pt-2">
                {hasValidContent(course?.coursemodule) ? (
                  <div className="course-content-card">
                    <div className="course-content-card-header">
                      <h5 className="course-content-card-title">
                        <i className="now-ui-icons design_app text-primary"></i>
                        Course Learning Modules &amp; Topics
                      </h5>
                    </div>
                    <div className="course-content-card-body">
                      {sectionContent(course.coursemodule)}
                    </div>
                  </div>
                ) : (
                  <TabEmptyState
                    icon="now-ui-icons design_app"
                    iconColor="#2ca8ff"
                    iconBg="rgba(44, 168, 255, 0.1)"
                    badge="Learning Units"
                    title="No Modules Available"
                    description="Learning units, weekly modules, and lecture presentations have not been published for this course yet."
                    tip="Weekly modules and lesson activities will appear here as your instructor unlocks them throughout the term."
                  />
                )}
              </div>
            </Tab>

            {/* 5. Grades Tab */}
            <Tab
              eventKey="grades"
              title={
                <span>
                  <i className="now-ui-icons business_chart-bar-32"></i> Grades
                </span>
              }
            >
              <div className="pt-2">
                {loadingGrades ? (
                  <div className="course-empty-state-card">
                    <Spinner
                      color="primary"
                      style={{ width: "3rem", height: "3rem" }}
                    />
                    <h4 className="mt-3 fw-bold text-dark">
                      Loading Course Grades...
                    </h4>
                    <p className="text-muted">
                      Retrieving assignment scores, quizzes, and grade records.
                    </p>
                  </div>
                ) : examsData.rows.length === 0 ? (
                  <TabEmptyState
                    icon="now-ui-icons business_chart-bar-32"
                    iconColor="#2c7be5"
                    iconBg="rgba(44, 123, 229, 0.1)"
                    badge="Assessments &amp; Scores"
                    title="No Grades Recorded Yet"
                    description="There are currently no graded quizzes, homework submissions, or exam scores recorded for this course."
                    tip="Your assessment grades and instructor feedback will appear here as submissions are completed and graded."
                  />
                ) : (
                  <div className="course-content-card">
                    <div className="course-content-card-header">
                      <h5 className="course-content-card-title">
                        <i className="now-ui-icons business_chart-bar-32 text-primary"></i>
                        {course?.name || "Course"} Gradebook &amp; Assessments
                      </h5>
                    </div>
                    <CardBody className="bg-light p-3">
                      <MDBDataTableV5
                        hover
                        paging={false}
                        responsive
                        striped
                        data={examsData}
                        bordered
                        searching={false}
                        style={{ fontSize: "13pt" }}
                      />
                    </CardBody>
                    {totMarks > 0 && (
                      <CardFooter className="bg-white border-top py-3 px-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                          <p className="fs-5 p-0 m-0">
                            Total Score:{" "}
                            <span className="fs-6 text-primary fw-bolder">
                              {totScores}
                            </span>{" "}
                            | Total Marks:{" "}
                            <span className="fs-6 text-primary fw-bolder">
                              {totMarks}
                            </span>
                          </p>
                          <h5 className="text-primary fw-bold p-0 m-0">
                            Percentage:{" "}
                            {((totScores / totMarks) * 100).toFixed(2)} %
                          </h5>
                        </div>
                      </CardFooter>
                    )}
                  </div>
                )}
              </div>
            </Tab>

            {/* 6. Student Rating of Teaching Tab */}
            <Tab
              eventKey="srt"
              title={
                <span>
                  <i className="now-ui-icons ui-2_like"></i> Student Rating of
                  Teaching
                </span>
              }
            >
              <div className="pt-2">
                <TabEmptyState
                  icon="now-ui-icons ui-2_like"
                  iconColor="#e34a6f"
                  iconBg="rgba(227, 74, 111, 0.1)"
                  badge="Course Evaluation"
                  title="Teaching Evaluations Not Active"
                  description="Student Rating of Teaching (SRT) surveys are currently closed or have not been scheduled yet for this course."
                  tip="Student evaluations typically open towards the conclusion of each academic term to provide confidential feedback on your learning experience."
                />
              </div>
            </Tab>

            {/* 7. Help & Resources Tab */}
            <Tab
              eventKey="helpandresourses"
              title={
                <span>
                  <i className="now-ui-icons travel_info"></i> Help &amp; Resources
                </span>
              }
            >
              <div className="pt-2">
                {hasValidContent(course?.helpandresources) ? (
                  <div className="course-content-card">
                    <div className="course-content-card-header">
                      <h5 className="course-content-card-title">
                        <i className="now-ui-icons travel_info text-primary"></i>
                        Help, Tutoring &amp; Academic Resources
                      </h5>
                    </div>
                    <div className="course-content-card-body">
                      {sectionContent(course.helpandresources)}
                    </div>
                  </div>
                ) : (
                  <TabEmptyState
                    icon="now-ui-icons travel_info"
                    iconColor="#f59e0b"
                    iconBg="rgba(245, 158, 11, 0.1)"
                    badge="Academic Support"
                    title="No Resources Listed"
                    description="No supplementary reference materials, office hour schedules, or tutoring links have been listed for this course yet."
                    tip="If you need academic guidance, tutoring, or support with coursework, please reach out directly to your course instructor or academic advisor."
                  />
                )}
              </div>
            </Tab>

            {/* 8. Google Drive Tab */}
            <Tab
              eventKey="googledrive"
              title={
                <span>
                  <i className="now-ui-icons arrows-1_cloud-download-93"></i> Google
                  Drive
                </span>
              }
            >
              <div className="pt-2">
                {driveUrl ? (
                  <TabEmptyState
                    icon="now-ui-icons arrows-1_cloud-download-93"
                    iconColor="#0F9D58"
                    iconBg="rgba(15, 157, 88, 0.1)"
                    badge="Shared Cloud Storage"
                    title="Google Drive Folder Available"
                    description="Access course lecture slides, shared spreadsheets, handouts, and study archives directly on Google Drive."
                    action={
                      <a
                        href={driveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success btn-round px-4 py-2 fw-bold"
                      >
                        <i className="now-ui-icons arrows-1_cloud-download-93 me-2"></i>
                        Open Course Google Drive Folder
                      </a>
                    }
                  />
                ) : (
                  <TabEmptyState
                    icon="now-ui-icons arrows-1_cloud-download-93"
                    iconColor="#0F9D58"
                    iconBg="rgba(15, 157, 88, 0.1)"
                    badge="Shared Cloud Storage"
                    title="No Google Drive Folder Linked"
                    description="A shared Google Drive repository has not been linked to this course."
                    tip="When your instructor links a Google Drive folder for class recordings, datasets, or submissions, the link will be accessible here."
                  />
                )}
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default CourseGrading;
